// Everything a dragged object may snap against on one mounting surface.
//
// This is the assembly point: `surfaceFootprints` says what is on a surface,
// `surfaceMirror` says how a pose maps onto a parallel one, and the snap
// engine turns footprints into candidate positions. Joining them is small but
// it is NOT obvious, and it had better happen in exactly one place — there
// are two live drag paths onto a wall (a panel or TV in useObjectDrag, a
// window in WindowGlass) and mobile will want a third. A wall element that
// snaps differently depending on which of them is dragging it is the same
// class of bug as a wall element that does not snap at all.

import type { EditorDoc } from '@roomtreatment/core';
import {
  alignmentCandidates, betweenCenterCandidates, dedupeCandidates,
  gapMirrorCandidates,
  type AxisFootprint, type SnapCandidate,
} from '@roomtreatment/floorplan-core';
import { surfaceKey, type SurfaceFrame } from './panelSurface';
import { surfaceFootprints } from './surfaceFootprints';
import { heightPeerSurfaces, mirrorSurfaces, projectPoseOntoSurface } from './surfaceMirror';

export interface SurfaceSnapTargets {
  /** Candidate positions for the dragged object's CENTRE along each axis. */
  u: SnapCandidate[];
  v: SnapCandidate[];
  /** The raw neighbour footprints behind them. `equalSpacingCandidates`
   *  depends on the live cursor position, so a caller re-derives it from
   *  these every move rather than getting it prebuilt here. */
  uFoot: AxisFootprint[];
  vFoot: AxisFootprint[];
}

export interface SurfaceSnapOptions {
  /** The dragged object, which must never measure against itself. */
  excludeId?: string;
  /** The dragged object's in-plane half-extents, already spun. Candidates are
   *  positions for its centre, so most of them depend on how big it is —
   *  "abut the TV's left edge" is a different place for a wide panel than a
   *  narrow one. */
  halfU: number;
  halfV: number;
}

/**
 * Build the full candidate list for a drag on `frame`.
 *
 * Five families, in the order a user would rank them:
 *
 *   alignment      every other wall element's centre, the positions that abut
 *                  its edges, and the ones that line edges up flush — plus
 *                  the surface's own two flush positions and its centreline.
 *   mirror-facet   the same spot on a parallel surface: the facing wall, the
 *                  floor under a ceiling panel. Projected through world
 *                  space, never by copying u across — facing walls have
 *                  opposite u directions, so the same place in the room is a
 *                  different u on each. See surfaceMirror.ts.
 *   height peers   the same idea restricted to v and widened to EVERY other
 *                  wall rather than only parallel ones. Every wall frame is
 *                  anchored at floor level with v = UP, so v is height above
 *                  the floor everywhere and running a consistent panel height
 *                  around a room needs no parallelism at all.
 *   gap-mirror     a gap reproduced on the far side of whatever made it —
 *                  "the same distance from the TV's other edge".
 *   gap-center     the midpoint of each clear run between two neighbours.
 *
 * Deduped before returning. With this many families, and with `mirror-facet`
 * competing on equal footing with literal edges, coincident candidates are
 * routine rather than exotic: a symmetric room drops a mirror right on top of
 * a real edge constantly.
 *
 * Everything here is fixed for the whole gesture, so callers build it once at
 * pointer-down.
 */
export function surfaceSnapTargets(
  doc: EditorDoc,
  frame: SurfaceFrame,
  surfaces: SurfaceFrame[],
  { excludeId, halfU, halfV }: SurfaceSnapOptions,
): SurfaceSnapTargets {
  const uBounds = { min: 0, max: frame.uMax };
  const vBounds = { min: 0, max: frame.vMax };

  const here = surfaceFootprints(doc, frame, excludeId);
  const uFoot: AxisFootprint[] = here.map((f) => ({ objectId: f.objectId, center: f.u, half: f.hu }));
  const vFoot: AxisFootprint[] = here.map((f) => ({ objectId: f.objectId, center: f.v, half: f.hv }));

  const u = alignmentCandidates(halfU, uFoot, uBounds);
  const v = alignmentCandidates(halfV, vFoot, vBounds);

  // A mirrored position that lands off the end of the target surface is not a
  // place anything can go — the facing wall of an L-shaped room is routinely
  // shorter than the one you are dragging on.
  const pushMirror = (list: SnapCandidate[], value: number, objectId: string, max: number) => {
    if (value < 0 || value > max) return;
    list.push({ value, source: { kind: 'mirror-facet', objectId } });
  };

  // A parallel facet contributes on both axes; any other wall contributes
  // height only. `seen` stops the facing wall — which is in both lists —
  // being walked twice.
  const seen = new Set<string>();
  for (const other of mirrorSurfaces(surfaces, frame)) {
    seen.add(surfaceKey(other.ref));
    for (const f of surfaceFootprints(doc, other, excludeId)) {
      const m = projectPoseOntoSurface(other, frame, f.u, f.v);
      pushMirror(u, m.u, f.objectId, frame.uMax);
      pushMirror(v, m.v, f.objectId, frame.vMax);
    }
  }
  for (const other of heightPeerSurfaces(surfaces, frame)) {
    if (seen.has(surfaceKey(other.ref))) continue;
    for (const f of surfaceFootprints(doc, other, excludeId)) {
      pushMirror(v, projectPoseOntoSurface(other, frame, f.u, f.v).v, f.objectId, frame.vMax);
    }
  }

  u.push(...gapMirrorCandidates(halfU, uFoot, uBounds));
  u.push(...betweenCenterCandidates(halfU, uFoot, uBounds));
  v.push(...gapMirrorCandidates(halfV, vFoot, vBounds));
  v.push(...betweenCenterCandidates(halfV, vFoot, vBounds));

  return { u: dedupeCandidates(u), v: dedupeCandidates(v), uFoot, vFoot };
}
